/**
 * @file GroupsExplorerPage.tsx
 * @description Custom groups/teams explorer page that mirrors the layout
 * of the /docs (TechDocsIndexPage) and /api-docs (ApiExplorerPage) pages.
 *
 * Uses PageWithHeader + CatalogFilterLayout for a consistent portal experience.
 */

import {
  PageWithHeader,
  Content,
  ContentHeader,
  SupportButton,
} from '@backstage/core-components';
import { useApi, configApiRef } from '@backstage/core-plugin-api';
import { CatalogTable } from '@backstage/plugin-catalog';
import type { CatalogTableRow } from '@backstage/plugin-catalog';
import {
  EntityListProvider,
  CatalogFilterLayout,
  EntityKindPicker,
  EntityTypePicker,
  UserListPicker,
  EntityTagPicker,
  EntityRefLink,
} from '@backstage/plugin-catalog-react';

const defaultColumns = [
  CatalogTable.columns.createTitleColumn({ hidden: true }),
  {
    ...CatalogTable.columns.createNameColumn({ defaultKind: 'Group' }),
    width: '32%',
    cellStyle: { minWidth: 220 },
    render: ({ entity }: CatalogTableRow) => (
      <EntityRefLink entityRef={entity} defaultKind="Group" hideIcon />
    ),
  },
  CatalogTable.columns.createSpecTypeColumn(),
  CatalogTable.columns.createMetadataDescriptionColumn(),
  CatalogTable.columns.createTagsColumn(),
];

export const GroupsExplorerPage = () => {
  const configApi = useApi(configApiRef);
  const orgName =
    configApi.getOptionalString('organization.name') ?? 'Univerus';

  return (
    <PageWithHeader
      themeId="teams"
      title="Teams"
      subtitle={`Teams and groups across ${orgName}`}
      pageTitleOverride="Teams"
    >
      <Content>
        <ContentHeader title="">
          <SupportButton>
            Browse teams, departments, and organizational groups.
          </SupportButton>
        </ContentHeader>
        <EntityListProvider>
          <CatalogFilterLayout>
            <CatalogFilterLayout.Filters>
              <EntityKindPicker initialFilter="group" hidden />
              <EntityTypePicker />
              <UserListPicker initialFilter="all" />
              <EntityTagPicker />
            </CatalogFilterLayout.Filters>
            <CatalogFilterLayout.Content>
              <CatalogTable columns={defaultColumns} />
            </CatalogFilterLayout.Content>
          </CatalogFilterLayout>
        </EntityListProvider>
      </Content>
    </PageWithHeader>
  );
};

export default GroupsExplorerPage;
